import * as DocumentApi from "services/api/DocumentApi"
import { DocumentToUpload, UploadedDocument } from "models"
import { sanitizeFilename } from "utils"
import { set } from "lodash"

export const uploadFile = async (file: DocumentToUpload | UploadedDocument, blob: Blob) => {
	return new Promise(async (resolve, reject) => {
		try {
			if ((file as UploadedDocument).document_data?.url) {
				resolve({
					name: (file as UploadedDocument).document_data.custom_name,
					fileAlreadyUploaded: file,
				})
				return
			}

			// Sanitize filename
			const sanitizedFile = set(
				file,
				"document.custom_name",
				sanitizeFilename((file as DocumentToUpload).document.custom_name, {
					forceAlphanumeric: true,
				})
			)

			// Get S3 url
			const { res: S3url, getError: getS3UrlError } = await DocumentApi.getS3Url(
				sanitizedFile as DocumentToUpload
			)
			if (getS3UrlError()) throw getS3UrlError()

			// issue with axios / blob / header authorization
			// https://github.com/axios/axios/issues/1569
			const uploadedFile = await fetch(S3url.document_upload_url, {
				method: "PUT",
				headers: { "Content-type": "" },
				body: blob,
			})

			resolve(uploadedFile)
		} catch (error) {
			reject(error)
		}
	})
}

// convert file object into Blob through its array buffer
// https://developer.mozilla.org/en-US/docs/Web/API/Blob/arrayBuffer
// since File is already an instance of Blob: file instanceof Blob // true
export const convertFileToBlob = async (file: File) => {
	const fileArrayBuffer = await file.arrayBuffer()
	return new Blob([new Uint8Array(fileArrayBuffer)], { type: file.type })
}

// Check that the file really is th extension it pretends to be, and
// is not a js file or an exe renamed.
export const checkFileIntegrity = async (file: File) => {
	return new Promise<boolean>(async (resolve) => {
		if (!file.type) return resolve(false)

		const reader = new FileReader()
		reader.addEventListener("loadend", (event) => {
			// prettier-ignore
			// (why do you do that prettier 😭 ? )
			// https://en.wikipedia.org/wiki/List_of_file_signatures
			const subarrayToCheck = new Uint8Array(event.target.result as ArrayBuffer).subarray(0, 4)
			const header = subarrayToCheck.reduce((acc, curr) => `${acc}${curr.toString(16)}`, "")
			const calculatedType = (() => {
				switch (header) {
					case "89504e47":
						return "image/png"
					case "47494638":
						return "image/gif"
					case "ffd8ffe0":
					case "ffd8ffe1":
					case "ffd8ffe2":
					case "ffd8ffe3":
					case "ffd8ffe8":
					case "ffd8ffdb":
						return "image/jpeg"
					case "25504446":
						return "application/pdf"
					default:
						return "unknown"
				}
			})()

			if (calculatedType === file.type) resolve(true)
			else resolve(false)
		})
		reader.readAsArrayBuffer(file)
	})
}
